import React, { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useUrlLocalizer } from 'tradera-lang';
import ALink from 'tradera-components/alink/alink';
import Home from '@tradera/blueprint/theme/icons2/home.svg?jsx';

const Breadcrumbs = ({
    categoryPath,
    categoryTree,
    className = '',
    handleClick,
    inSPA = false
}) => {
    const { toLocalizedUrl } = useUrlLocalizer();
    const breadcrumbRef = useRef(null);
    // Use path if provided otherwise parse path from tree
    let parsedCategoryPath = useMemo(() => {
        if (categoryPath) {
            return categoryPath;
        } else if (categoryTree) {
            const path = resolveActiveCategoryPath(categoryTree);
            // If empty path, fall back to first root category
            if (path.length > 0) {
                return path;
            } else {
                return [categoryTree];
            }
        } else {
            return [];
        }
    }, [categoryPath, categoryTree]);

    useEffect(() => {
        breadcrumbRef.current.scrollLeft =
            breadcrumbRef.current.scrollWidth -
            breadcrumbRef.current.clientWidth;
    }, [parsedCategoryPath]);

    return (
        <div className={`d-flex flex-row-reverse overflow-x-auto ${className}`}>
            <ol
                itemScope
                itemType="https://schema.org/BreadcrumbList"
                ref={breadcrumbRef}
                className="list-inline d-flex flex-md-wrap flex-nowrap align-items-center text-inter m-0 h6 font-weight-normal overflow-x-auto hide-scrollbar mr-2"
                data-breadcrumb="">
                <li>
                    <ALink
                        href={toLocalizedUrl('/')}
                        useLink={inSPA}
                        aria-label="Home"
                        data-ga-category="Search"
                        data-ga-action="Breadcrumb"
                        data-ga-label="Home">
                        <Home className="icon icon icon-dark align-text-bottom" />
                    </ALink>
                </li>
                {parsedCategoryPath.map((category, index) => (
                    <BreadcrumbItem
                        key={index}
                        url={window.location.origin + category.url}
                        inSPA={inSPA}
                        title={category.name}
                        microDataPositionProperty={index + 1}
                        handleClick={
                            handleClick ? () => handleClick(category) : null
                        }
                    />
                ))}
            </ol>
        </div>
    );
};

Breadcrumbs.propTypes = {
    categoryPath: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            url: PropTypes.string,
            name: PropTypes.string
        })
    ),
    categoryTree: PropTypes.shape({
        status: PropTypes.string,
        children: PropTypes.arrayOf(PropTypes.object),
        id: PropTypes.number,
        url: PropTypes.string,
        name: PropTypes.string
    }),
    className: PropTypes.string,
    handleClick: PropTypes.func,
    inSPA: PropTypes.bool
};

/**
 * Recursively search tree for active category, return list of it and its parents
 *
 * @param {object} category - Category tree
 * @param {object[]} parents - Parent categories to current tree
 */
const resolveActiveCategoryPath = (category, parents = []) => {
    if (category.status === 'active') {
        return [...parents, category];
    } else {
        if (category.children) {
            for (const child of category.children) {
                const path = resolveActiveCategoryPath(child, [
                    ...parents,
                    category
                ]);
                if (path.length > 0) return path;
            }
        }
        return [];
    }
};

/**
 * Creates a breadcrumb item with Microdata attributes.
 * Microdata can be tested with Google's Structured Data Testing Tool here:
 * https://search.google.com/structured-data/testing-tool?utm_campaign=devsite&utm_medium=microdata&utm_source=breadcrumb
 * Please note that it will give errors if href is contains localhost:5454.
 */
const BreadcrumbItem = ({
    handleClick,
    inSPA,
    microDataPositionProperty,
    title,
    url
}) => {
    const isFirstElement = microDataPositionProperty === 1;
    const gaLabel = isFirstElement
        ? 'All'
        : `L${microDataPositionProperty - 1}`;

    return (
        <li
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
            className="list-inline-item text-nowrap">
            <span className={`ml-1 ${isFirstElement ? 'd-inline' : ''}`}>
                {'->'}
            </span>
            {handleClick ? (
                <span
                    className="btn-link cursor-pointer"
                    data-ga-category="Search"
                    data-ga-action="Breadcrumb"
                    data-ga-label={gaLabel}
                    data-test-search-breadcrumb-link=""
                    aria-labelledby="breadcrumb-link-title"
                    onClick={handleClick}>
                    <TextElement>{title}</TextElement>
                </span>
            ) : (
                <ALink
                    useLink={inSPA}
                    data-ga-category="Search"
                    data-ga-action="Breadcrumb"
                    data-ga-label={gaLabel}
                    itemType="https://schema.org/Thing"
                    itemProp="item"
                    href={url}>
                    <TextElement>{title}</TextElement>
                </ALink>
            )}
            <meta itemProp="position" content={microDataPositionProperty} />
        </li>
    );
};

const TextElement = ({ children }) => (
    <span itemProp="name" className="text-inter-medium ml-1 text-primary">
        {children}
    </span>
);

export default Breadcrumbs;
